import React, { useMemo } from 'react';
import {
  EdiBucketValidation,
  EdiInvoiceBucket,
  EdiInvoiceBucketGroup,
} from '../services/pendingEdiInvoices.service';

// =============================================================================
// EdiInvoiceBucketPanel
// =============================================================================
// The clickable bucket list for EDI Pending Invoices — V1's Accounting-dashboard
// panel (`dash_Acct_PendingEDIInvoice`), which was a two-column table under two
// section headers, "Internal" and "Kleinschmidt".
//
// Purely presentational: it takes the bucket rows and reports a click. Kept separate
// from the page (like EdiBucketPanel) because an Accounting dashboard is the obvious
// next place to embed it.
//
// THE COUNT CHIP CARRIES TWO SIGNALS, not one. Green has always meant "this partner has
// invoices ready". Red means some of them would be REFUSED if you sent the bucket now —
// which is the thing an operator wants to know BEFORE selecting all and pressing Send,
// and which previously only appeared after clicking in. Validation is slow (it checks
// every pending invoice), so it arrives separately and the chip recolours when it does:
// until then the bucket reads green, exactly as it did before this existed.
//
// EMPTY BUCKETS STAY VISIBLE. V1 paints every configured partner whether or not it has
// work, and that is right: a partner you expect invoices for showing zero is
// information. They are dimmed rather than dropped, and `hideEmpty` lets the operator
// collapse them away when they want the worklist only.

const GROUP_TITLES: Record<EdiInvoiceBucketGroup, string> = {
  internal: 'Internal',
  kleinschmidt: 'Kleinschmidt',
};

export interface EdiInvoiceBucketPanelProps {
  buckets: EdiInvoiceBucket[];
  loading?: boolean;
  onSelect: (key: string) => void;
  /** Bucket key of the open bucket, for highlighting. */
  selectedKey?: string | null;
  /** Drop zero-count buckets from the list. */
  hideEmpty?: boolean;
  /** Per-bucket validation, keyed by bucket key. Absent until the (slow) sweep lands;
   *  a bucket missing from the map is simply not coloured yet. */
  validation?: Map<string, EdiBucketValidation>;
  /** Suppress the panel's frame + title so a host tile supplies the chrome. */
  embedded?: boolean;
  className?: string;
}

const EdiInvoiceBucketPanel: React.FC<EdiInvoiceBucketPanelProps> = ({
  buckets,
  loading = false,
  onSelect,
  selectedKey,
  hideEmpty = false,
  embedded = false,
  validation,
  className,
}) => {
  const groups = useMemo(() => {
    const order: EdiInvoiceBucketGroup[] = ['internal', 'kleinschmidt'];
    return order
      .map((g) => {
        const rows = buckets.filter((b) => b.group === g && (!hideEmpty || b.count > 0));
        return { group: g, rows, total: rows.reduce((s, b) => s + b.count, 0) };
      })
      .filter((g) => g.rows.length > 0);
  }, [buckets, hideEmpty]);

  const total = useMemo(() => buckets.reduce((s, b) => s + b.count, 0), [buckets]);

  const body = (
    <div className={embedded ? '' : 'overflow-y-auto'}>
      {loading && buckets.length === 0 && (
        <div className="px-3 py-6 text-sm text-muted">Loading buckets…</div>
      )}
      {!loading && groups.length === 0 && (
        <div className="px-3 py-6 text-sm text-muted">
          {hideEmpty ? 'No buckets have pending invoices.' : 'No buckets configured.'}
        </div>
      )}

      {groups.map((g) => (
        <div key={g.group}>
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-label">
              {GROUP_TITLES[g.group]}
            </span>
            <span
              className={[
                'text-xs font-semibold tabular-nums rounded px-1.5',
                g.total > 0 ? 'bg-ok text-ok-fg' : 'text-muted',
              ].join(' ')}
            >
              {g.total}
            </span>
          </div>

          {g.rows.map((b) => {
            const selected = selectedKey === b.key;
            const empty = b.count === 0;
            const v = validation?.get(b.key);
            const bad = (v?.errors ?? 0) > 0;
            // A sweep that FAILED for this bucket is not a clean bucket, it is an
            // unknown one, and it must not keep wearing the green it had before.
            const unknown = !!v?.error;
            const title = [
              b.note,
              bad && `${v!.errors} of ${v!.invoices} would be refused if sent now`,
              unknown && `Could not be validated: ${v!.error}`,
              v?.skippedRules.length
                && `Not fully validated — ${v.skippedRules.length} rule(s) could not be evaluated`,
            ].filter(Boolean).join(' · ');
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => onSelect(b.key)}
                title={title || undefined}
                className={[
                  'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                  'border-b border-gray-100 dark:border-gray-800',
                  selected
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                  empty ? 'text-muted' : 'text-label',
                ].join(' ')}
              >
                <span className="truncate">{b.label}</span>
                {/* Green on a non-zero count, matching V1's doColorClass('>0') cell — the
                    at-a-glance "this partner has invoices ready" signal. Red when some of
                    those invoices would be refused, showing how many of how many, so the
                    chip answers "is there work" and "is any of it broken" at once.
                    Semantic token pairs, so both hold up in either theme. */}
                <span
                  className={[
                    'shrink-0 min-w-[2.25rem] text-right tabular-nums rounded px-1.5 py-0.5',
                    empty
                      ? 'text-gray-400 dark:text-gray-600'
                      : bad
                        ? 'bg-danger text-danger-fg font-bold'
                        : unknown
                          ? 'bg-warn text-warn-fg font-semibold'
                          : 'bg-ok text-ok-fg font-semibold',
                  ].join(' ')}
                >
                  {bad ? `${v!.errors}/${b.count}` : b.count}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );

  if (embedded) return <div className={className}>{body}</div>;

  return (
    <div
      className={[
        'flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-700',
        'bg-white dark:bg-gray-900 overflow-hidden',
        className || '',
      ].join(' ')}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <span className="text-sm font-semibold text-label">Bill To</span>
        <span className="text-sm font-semibold text-label tabular-nums">{total}</span>
      </div>
      {body}
    </div>
  );
};

export default EdiInvoiceBucketPanel;
